import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import { isAemEditMode } from '@owc-utils/AEM';

const ANCHOR_ID_ATTR_NAME = 'data-anchor-id';
const AEM_SCROLL_VIEW_CONTENT_ELEMENT_ID = 'ContentScrollView';
let isListenerAdded = false;

const getAnchorId = (href: string | null): string | null => {
  if (href) {
    const hashSignIndex: number = href.lastIndexOf('#');

    // Check if hash is at index 0
    let shouldExtractHashFromHref: boolean = hashSignIndex === 0;

    // Hash is not at index 0, so try to extract it by comparing link page and current page
    if (!shouldExtractHashFromHref) {
      const currentLinkPage = href.substring(href.lastIndexOf('/') + 1, hashSignIndex);
      shouldExtractHashFromHref = window.location.href.includes(currentLinkPage);
    }

    // Ready to go, extract hash
    if (shouldExtractHashFromHref) {
      return href.slice(hashSignIndex + 1);
    }
  }
  return null;
};

const scrollAnchoredElementIntoView = (href: string): void => {
  const anchorId = getAnchorId(href);
  if (anchorId) {
    const anchoredEl = document.querySelector(`[${ANCHOR_ID_ATTR_NAME}="${anchorId}"]`) as HTMLElement;
    if (anchoredEl) {
      // Check if the element with AEM_SCROLL_VIEW_CONTENT_ELEMENT_ID exists in the parent document
      // to determine whether content scroll view element should be scrolled or window.
      const aemScrollableView: HTMLElement | null = window.parent.document.getElementById(
        AEM_SCROLL_VIEW_CONTENT_ELEMENT_ID,
      );
      const scrollableElement: HTMLElement | Window = aemScrollableView || window;
      scrollableElement.scroll({ top: anchoredEl.offsetTop, left: 0, behavior: 'smooth' });
    }
  }
};

const mixin: ThisTypedComponentOptionsWithRecordProps<Vue, {}, {}, {}, {}, {}, {}, {}> = {
  mounted() {
    if (!isListenerAdded && !isAemEditMode(this.$parent!)) {
      isListenerAdded = true;

      // Add click listener to the document, so event bubbling occurs and anchor elements can be targeted
      document.addEventListener('click', (event: MouseEvent) => {
        // We need to ensure an anchor is part of event bubbling
        const clickedAnchors = event
          .composedPath()
          .filter((el: EventTarget) => (el as HTMLAnchorElement).tagName === 'A') as HTMLAnchorElement[];
        if (clickedAnchors.length) {
          const targetAnchorElement: HTMLAnchorElement = clickedAnchors[0];
          const href: string = targetAnchorElement.getAttribute('href') || '';
          scrollAnchoredElementIntoView(href);
        }
      });

      // Add load listener once, to scroll to an anchored element if anchor ID passed in the URL
      window.addEventListener(
        'load',
        () => {
          const hash: string = window.location.hash;
          if (hash) {
            // Need to defer scrolling a bit because sometimes on mobile devices
            // an element coordinates are calculated incorrectly, so the view gets
            // scrolled to the wrong position and an anchored element is not visible.
            requestAnimationFrame(() => scrollAnchoredElementIntoView(hash));
          }
        },
        {
          once: true,
        },
      );
    }
  },
};

export default mixin;
